/**
 * Single sign-on for a customer, set up by a system administrator.
 *
 * Hospitals sign in through Microsoft Entra ID (Azure AD). Their IT team
 * registers StandingDocs as an application and hands us three things — the
 * tenant id, the client id and a client secret — and we hand them one: the
 * redirect URI to register. Everything else about the tenant (its endpoints
 * and signing keys) is read from Microsoft's discovery document, so there is
 * nothing to transcribe.
 *
 * Order of operations, which the section walks through top to bottom:
 *   1. the email domains that belong to this hospital, marked verified
 *      (DNS, or on our own authority — the contract names the domain);
 *   2. the Entra registration;
 *   3. who gets in: known members only, or anyone Entra vouches for, with
 *      which roles;
 *   4. switch it on, then choose whether the emailed link stays available.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES, isRole, type Role } from '../../types/roles';
import { DOMAIN_MAX, ID_MAX, SECRET_MAX } from '../../types/fieldLimits';
import { formatDay } from '../../shared/dates';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import { GroupCard } from '../../shared/GroupCard';

export interface SsoDomain {
  domain: string;
  verified: boolean;
  verificationToken: string;
  createdAt: string;
}

export interface SsoMapping { idpGroup: string; roles: string[] }

export interface SsoConfig {
  org: { id: string; name: string; status: string };
  authMode: 'password_only' | 'mixed' | 'sso_only' | 'social_only';
  allowedDomains: string[];
  sso: {
    enabled: boolean;
    provider: 'saml' | 'oidc' | null;
    oidc: { issuer?: string; clientId?: string; tenantId?: string; authorizationUrl?: string; tokenUrl?: string; jwksUri?: string; hasClientSecret: boolean; clientSecretExpiresAt?: string | null } | null;
    jitProvisioning: { enabled: boolean; defaultRoles: string[]; autoActivate: boolean };
    roleMapping: { enabled: boolean; sourceAttribute: string; mappings: SsoMapping[]; fallbackRoles: string[] };
  };
  domains: SsoDomain[];
  callbackUrls: { oidc: { redirectUri: string; scopes: string[] }; requiredClaims: string[]; optionalClaims: string[] };
  validation: { isReady: boolean; issues: string[]; hasDomains: boolean; hasIdpConfig: boolean; hasClientSecret: boolean };
  updatedAt: string | null;
}

export interface TestResult { category: string; check: string; status: 'pass' | 'warn' | 'fail'; message: string }

/** A mapping row as the form holds it: one group, one role. */
export interface MappingRow { idpGroup: string; appRole: Role }

export function rowsFromMappings(mappings: SsoMapping[] | undefined): MappingRow[] {
  return (mappings ?? []).map((m) => ({ idpGroup: m.idpGroup, appRole: (m.roles.find(isRole) ?? 'viewer') as Role }));
}

/** What the config says in one line, for the summary tag. */
export function ssoSummary(c: SsoConfig | undefined): { label: string; tone: 'ok' | 'warn' | 'neutral' } {
  if (!c) return { label: 'Not set up', tone: 'neutral' };
  if (c.sso.enabled) return { label: c.authMode === 'sso_only' ? 'On, SSO only' : 'On, with the email link', tone: 'ok' };
  if (c.validation.isReady) return { label: 'Ready to switch on', tone: 'warn' };
  return { label: 'Not set up', tone: 'neutral' };
}

const KEY = (orgId: string) => ['console-org-sso', orgId];

/** "Expires 2027-03-01 (164 days)", in warn or danger words when close or past. */
export function expiryLine(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'No expiry date on record.';
  const when = iso.slice(0, 10);
  const days = Math.floor((new Date(iso).getTime() - now.getTime()) / 86_400_000);
  if (days < 0) return `EXPIRED on ${when}: Microsoft sign-in is down until a new secret is saved.`;
  if (days <= 30) return `Expires ${when}, in ${days} ${days === 1 ? 'day' : 'days'}: replace it soon.`;
  return `Expires ${when} (${days} days).`;
}

export function SsoSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const config = useQuery({
    queryKey: KEY(orgId),
    retry: false,
    queryFn: () => call<{ orgId: string }, SsoConfig>('sysAdminGetOrgSsoConfig', { orgId }),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: KEY(orgId) });

  const save = useMutation({
    mutationFn: (input: Record<string, unknown>) => call<Record<string, unknown>, unknown>('sysAdminSetOrgSsoConfig', { orgId, ...input }),
    onSuccess: refresh,
  });
  const domain = useMutation({
    mutationFn: (input: { domain: string; action: 'add' | 'remove' | 'verify' | 'trust' }) =>
      call<{ orgId: string; domain: string; action: string }, { success: boolean; verified?: boolean; message?: string; dnsRecord?: { host: string; value: string } }>('sysAdminManageOrgDomain', { orgId, ...input }),
    onSuccess: refresh,
  });
  const enable = useMutation({
    mutationFn: (enabled: boolean) => call<{ orgId: string; enabled: boolean }, unknown>('sysAdminEnableSso', { orgId, enabled }),
    onSuccess: refresh,
  });
  const mode = useMutation({
    mutationFn: (authMode: 'mixed' | 'sso_only') => call<{ orgId: string; authMode: string }, unknown>('sysAdminSetOrgAuthMode', { orgId, authMode }),
    onSuccess: refresh,
  });
  const test = useMutation({
    mutationFn: () => call<{ orgId: string }, { results: TestResult[] } | TestResult[]>('sysAdminTestOrgSsoConfig', { orgId }),
  });

  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [secretExpires, setSecretExpires] = useState('');
  const [jitEnabled, setJitEnabled] = useState(false);
  const [autoActivate, setAutoActivate] = useState(true);
  const [defaultRole, setDefaultRole] = useState<Role>('viewer');
  const [mappingEnabled, setMappingEnabled] = useState(false);
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Seeded once from the saved registration, while rendering (as
  // SystemEmailPage does) rather than in an effect that drew it empty first.
  if (config.data && !loaded) {
    const c = config.data;
    setTenantId(c.sso.oidc?.tenantId ?? '');
    setClientId(c.sso.oidc?.clientId ?? '');
    setJitEnabled(c.sso.jitProvisioning.enabled);
    setAutoActivate(c.sso.jitProvisioning.autoActivate);
    const dr = c.sso.jitProvisioning.defaultRoles.find(isRole);
    if (dr) setDefaultRole(dr as Role);
    setMappingEnabled(c.sso.roleMapping.enabled);
    setRows(rowsFromMappings(c.sso.roleMapping.mappings));
    setLoaded(true);
  }

  const c = config.data;
  const summary = ssoSummary(c);
  const busy = save.isPending || domain.isPending || enable.isPending || mode.isPending;
  const error = config.error ?? save.error ?? domain.error ?? enable.error ?? mode.error ?? test.error;

  function saveRegistration(e: FormEvent) {
    e.preventDefault();
    if (!tenantId.trim() || !clientId.trim()) return;
    const payload: Record<string, unknown> = {
      ssoType: 'oidc',
      oidc: { tenantId: tenantId.trim(), clientId: clientId.trim(), ...(clientSecret ? { clientSecret } : {}), ...(secretExpires ? { clientSecretExpiresAt: secretExpires } : {}) },
      jit: { enabled: jitEnabled, autoActivate, defaultRoles: [defaultRole] },
      roleMapping: {
        enabled: mappingEnabled,
        mappings: rows.filter((r) => r.idpGroup.trim()).map((r) => ({ idpGroup: r.idpGroup.trim(), appRole: r.appRole })),
        defaultRole,
      },
    };
    save.mutate(payload, { onSuccess: () => { setClientSecret(''); setNotice('Saved. The tenant\'s endpoints were read from Microsoft.'); } });
  }

  function addDomain(e: FormEvent) {
    e.preventDefault();
    const d = newDomain.trim().toLowerCase().replace(/^@/, '');
    if (!d) return;
    domain.mutate({ domain: d, action: 'add' }, {
      onSuccess: (r) => {
        setNewDomain('');
        setNotice(r.dnsRecord
          ? `Added ${d}. To verify by DNS, add a TXT record at ${r.dnsRecord.host} with the value ${r.dnsRecord.value}, or mark it verified below.`
          : `Added ${d}.`);
      },
    });
  }

  const testResults: TestResult[] = Array.isArray(test.data) ? test.data : (test.data?.results ?? []);

  return (
    <GroupCard id="sso-title" title="Single sign-on" level={2}
      meta={<span className={`${page.tag} ${summary.tone === 'ok' ? page.ok : summary.tone === 'warn' ? page.warn : page.neutral}`}>{summary.label}</span>}>
      <div className={`${page.notice} ${page.noticeInfo}`} role="note" style={{ marginTop: 'var(--sp-3)' }}>
        <strong>Not released to customers.</strong> This has never carried a real sign-in: no run against a mock
        issuer, none against a real tenant, and no organization has ever had it on. Customers see no panel and
        their own System Admin is refused at the callable. It is here so that we can run the validation pass.
        Do not switch it on for a customer before that pass is done.
      </div>
      <p style={{ color: 'var(--ink-2)', lineHeight: 1.6, maxWidth: '68ch', margin: 'var(--sp-3) 0 var(--sp-4)' }}>
        Microsoft Entra ID (Azure AD). The hospital's IT team registers StandingDocs as an application and gives us
        the tenant id, the client id and a client secret; we give them the redirect URI below. Staff whose email domain
        is listed here are sent to Microsoft to sign in instead of being emailed a link.
      </p>

      {notice && <div className={`${page.notice} ${page.noticeInfo}`} role="status">{notice}</div>}
      {error && (
        <div className={own.error} role="alert">
          {isCallFailure(error) ? error.message : 'Could not read or change the single sign-on settings.'}
        </div>
      )}

      {config.isLoading ? (
        <p className={page.itemMeta}>Loading…</p>
      ) : c ? (
        <>
          <h3 className={page.itemName} style={{ marginTop: 'var(--sp-4)' }}>What to give the hospital's Entra administrator</h3>
          <dl className={page.facts}>
            <div>
              <dt className={page.factTerm}>Redirect URI</dt>
              <dd className={page.factValue} style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, wordBreak: 'break-all' }}>{c.callbackUrls.oidc.redirectUri}</dd>
            </div>
            <div>
              <dt className={page.factTerm}>Platform</dt>
              <dd className={page.factValue}>Web (authorization code with PKCE and a client secret)</dd>
            </div>
            <div>
              <dt className={page.factTerm}>Claims</dt>
              <dd className={page.factValue}>email or preferred_username (required); name; groups (for role mapping)</dd>
            </div>
          </dl>

          <h3 className={page.itemName} style={{ marginTop: 'var(--sp-5)' }}>1. Email domains</h3>
          <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
            Addresses at these domains belong to this hospital. A domain must be verified before sign-on can be switched on.
          </p>
          {c.domains.length === 0 ? (
            <p className={page.itemMeta}>No domains yet.</p>
          ) : (
            <ul className={page.list}>
              {c.domains.map((d) => (
                <li key={d.domain} className={page.listItem}>
                  <div>
                    <div className={page.itemName}>
                      {d.domain}{' '}
                      <span className={`${page.tag} ${d.verified ? page.ok : page.warn}`} style={{ marginLeft: 8 }}>{d.verified ? 'Verified' : 'Not verified'}</span>
                    </div>
                    <div className={page.itemMeta}>Added {formatDay(d.createdAt)}{!d.verified && ` · TXT at _somexai-verification.${d.domain}`}</div>
                  </div>
                  <div className={page.actions}>
                    {!d.verified && (
                      <>
                        <button type="button" className={page.btn} disabled={busy} onClick={() => domain.mutate({ domain: d.domain, action: 'verify' }, { onSuccess: (r) => setNotice(r.message ?? (r.verified ? 'Verified.' : 'Not verified yet.')) })}>Check DNS</button>
                        <button type="button" className={page.btn} disabled={busy} onClick={() => domain.mutate({ domain: d.domain, action: 'trust' }, { onSuccess: () => setNotice(`${d.domain} marked verified on your authority; that is in the audit log.`) })}>Mark verified</button>
                      </>
                    )}
                    <button type="button" className={page.btn} disabled={busy} aria-label={`Remove ${d.domain}`} onClick={() => domain.mutate({ domain: d.domain, action: 'remove' }, { onSuccess: () => setNotice(`Removed ${d.domain}.`) })}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={addDomain} className={page.actions} style={{ marginTop: 'var(--sp-3)', alignItems: 'end' }}>
            <div>
              <label className={form.label} htmlFor={`sso-domain-${orgId}`}>Add a domain</label>
              <input id={`sso-domain-${orgId}`} className={form.input} placeholder="mercy.org" maxLength={DOMAIN_MAX} value={newDomain} onChange={(e) => setNewDomain(e.target.value)} disabled={busy} />
            </div>
            <button type="submit" className={page.btn} disabled={busy || !newDomain.trim()}>Add domain</button>
          </form>

          <form onSubmit={saveRegistration} style={{ marginTop: 'var(--sp-5)' }} aria-labelledby={`sso-reg-${orgId}`}>
            <h3 className={page.itemName} id={`sso-reg-${orgId}`}>2. Entra registration</h3>
            <div className={form.row} style={{ marginTop: 'var(--sp-3)' }}>
              <div className={form.field}>
                <label className={form.label} htmlFor={`sso-tenant-${orgId}`}>Tenant ID</label>
                <input id={`sso-tenant-${orgId}`} className={form.input} maxLength={ID_MAX} value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="11111111-2222-3333-4444-555555555555" disabled={busy} />
                <p className={form.hint}>The Directory (tenant) ID from the app registration's overview page.</p>
              </div>
              <div className={form.field}>
                <label className={form.label} htmlFor={`sso-client-${orgId}`}>Client ID</label>
                <input id={`sso-client-${orgId}`} className={form.input} maxLength={ID_MAX} value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={busy} />
                <p className={form.hint}>Entra shows it as &ldquo;Application (client) ID&rdquo;.</p>
              </div>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={`sso-secret-${orgId}`}>Client secret</label>
              <input id={`sso-secret-${orgId}`} className={form.input} type="password" autoComplete="off" maxLength={SECRET_MAX} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={c.sso.oidc?.hasClientSecret ? 'Saved. Enter a new one to replace it.' : 'The secret value, not its ID'} disabled={busy} />
              <p className={form.hint}>Kept in Secret Manager; never shown again.</p>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={`sso-secret-expires-${orgId}`}>Secret expires on</label>
              <input id={`sso-secret-expires-${orgId}`} className={form.input} type="date" maxLength={10} value={secretExpires} onChange={(e) => setSecretExpires(e.target.value)} disabled={busy} required={Boolean(clientSecret)} />
              <p className={form.hint}>
                {expiryLine(c.sso.oidc?.clientSecretExpiresAt)} Entra shows the date under Certificates &amp; secrets. We warn the System Admin 30, 14, 7 and 1 days ahead, and again once it has lapsed.
              </p>
            </div>
            {c.sso.oidc?.issuer && (
              <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-4)' }}>
                Issuer {c.sso.oidc.issuer}{c.sso.oidc.jwksUri ? ' · endpoints read from discovery' : ' · endpoints not yet read'}
              </p>
            )}

            <h3 className={page.itemName} style={{ marginTop: 'var(--sp-4)' }}>3. Who gets in</h3>
            <label className={form.check}>
              <input type="checkbox" checked={jitEnabled} onChange={(e) => setJitEnabled(e.target.checked)} disabled={busy} />
              <span>Let anyone Entra vouches for sign in, even if nobody invited them<span className={form.hint}>Off: only people already on the Staff page can sign in through Entra.</span></span>
            </label>
            {jitEnabled && (
              <>
                <label className={form.check}>
                  <input type="checkbox" checked={autoActivate} onChange={(e) => setAutoActivate(e.target.checked)} disabled={busy} />
                  <span>Activate them at once<span className={form.hint}>Off: they are created but wait for an administrator to activate them.</span></span>
                </label>
                <div className={form.field} style={{ maxWidth: 320 }}>
                  <label className={form.label} htmlFor={`sso-role-${orgId}`}>Role for a new person</label>
                  <select id={`sso-role-${orgId}`} className={form.select} value={defaultRole} onChange={(e) => setDefaultRole(e.target.value as Role)} disabled={busy}>
                    {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{ROLE_DISPLAY_NAMES[r]}</option>)}
                  </select>
                </div>
              </>
            )}
            <label className={form.check}>
              <input type="checkbox" checked={mappingEnabled} onChange={(e) => setMappingEnabled(e.target.checked)} disabled={busy} />
              <span>Give roles from Entra groups<span className={form.hint}>The app registration must send the groups claim. A group's object id or name, whichever Entra sends.</span></span>
            </label>
            {mappingEnabled && (
              <div style={{ margin: '0 0 var(--sp-4)' }}>
                {rows.map((r, i) => (
                  <div key={i} className={form.row} style={{ alignItems: 'end', marginBottom: 'var(--sp-2)' }}>
                    <div>
                      <label className={form.label} htmlFor={`sso-map-g-${orgId}-${i}`}>Entra group</label>
                      <input id={`sso-map-g-${orgId}-${i}`} className={form.input} maxLength={ID_MAX} value={r.idpGroup} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, idpGroup: e.target.value } : x)))} disabled={busy} />
                    </div>
                    <div>
                      <label className={form.label} htmlFor={`sso-map-r-${orgId}-${i}`}>Role</label>
                      <select id={`sso-map-r-${orgId}-${i}`} className={form.select} value={r.appRole} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, appRole: e.target.value as Role } : x)))} disabled={busy}>
                        {ASSIGNABLE_ROLES.map((role) => <option key={role} value={role}>{ROLE_DISPLAY_NAMES[role]}</option>)}
                      </select>
                    </div>
                    <button type="button" className={page.btn} aria-label={`Remove mapping ${i + 1}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} disabled={busy}>Remove</button>
                  </div>
                ))}
                <button type="button" className={page.btn} onClick={() => setRows([...rows, { idpGroup: '', appRole: 'viewer' }])} disabled={busy}>Add a group</button>
              </div>
            )}

            <div className={page.actions}>
              <button type="submit" className={`${page.btn} ${page.btnPrimary}`} disabled={busy || !tenantId.trim() || !clientId.trim()}>{save.isPending ? 'Saving…' : 'Save registration'}</button>
              <button type="button" className={page.btn} disabled={test.isPending} onClick={() => test.mutate()}>{test.isPending ? 'Checking…' : 'Check the setup'}</button>
            </div>
          </form>

          {testResults.length > 0 && (
            <ul className={page.list} style={{ marginTop: 'var(--sp-3)' }} aria-label="Setup checks">
              {testResults.map((r, i) => (
                <li key={i} className={page.listItem}>
                  <div>
                    <div className={page.itemName}>
                      <span className={`${page.tag} ${r.status === 'pass' ? page.ok : r.status === 'warn' ? page.warn : page.danger}`} style={{ marginRight: 8 }}>{r.status}</span>
                      {r.category} · {r.check}
                    </div>
                    <div className={page.itemMeta}>{r.message}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <h3 className={page.itemName} style={{ marginTop: 'var(--sp-5)' }}>4. Switch it on</h3>
          <ol className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)', paddingLeft: 18, lineHeight: 1.6 }}>
            <li>Turn it on. Staff see "Continue with Microsoft" on the sign-in page; the emailed link still works for everyone. Sign in through Microsoft yourself, on a second browser.</li>
            <li>After a couple of quiet weeks, "Require SSO only". Staff get no emailed links from then on. The System Admin always keeps the emailed link, so an Entra outage or an expired secret never locks the organization out of this page.</li>
            <li>Rollback is one click: "Allow the emailed link too", or turn single sign-on off.</li>
          </ol>
          <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)' }}>
            Not done here: removing leavers (that stays on the Staff page; there is no SCIM), and syncing Entra groups on a schedule (roles are mapped at each sign-in, additively).
          </p>
          {c.validation.issues.length > 0 && !c.sso.enabled && (
            <ul className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)', paddingLeft: 18 }}>
              {c.validation.issues.map((i) => <li key={i}>{i}</li>)}
            </ul>
          )}
          <div className={page.actions}>
            <button type="button" className={`${page.btn} ${c.sso.enabled ? '' : page.btnPrimary}`} disabled={busy || (!c.sso.enabled && !c.validation.isReady)} onClick={() => enable.mutate(!c.sso.enabled, { onSuccess: () => setNotice(c.sso.enabled ? 'Single sign-on is off. Staff use the emailed link.' : 'Single sign-on is on for the listed domains.') })}>
              {enable.isPending ? 'Saving…' : c.sso.enabled ? 'Turn single sign-on off' : 'Turn single sign-on on'}
            </button>
            {c.sso.enabled && (
              <button type="button" className={page.btn} disabled={busy} onClick={() => mode.mutate(c.authMode === 'sso_only' ? 'mixed' : 'sso_only', { onSuccess: () => setNotice(c.authMode === 'sso_only' ? 'The emailed link is available again alongside SSO.' : 'SSO only: the emailed link no longer works for these domains.') })}>
                {c.authMode === 'sso_only' ? 'Allow the emailed link too' : 'Require SSO only'}
              </button>
            )}
          </div>
        </>
      ) : null}
    </GroupCard>
  );
}
